import { useEffect, useMemo, useState } from 'react'

import { useMutation, useQuery } from 'react-query'

import type { Creator } from '@/modules/creators/types.ts'
import { purchaseService } from '@/modules/licenses/service/purchase-service.ts'
import { handleError } from '@/utils/error'

import type { BillingPlan, PurchaseContext } from '../types'

export function useGetLicense(
  creator: Creator | undefined,
  currentPlan: BillingPlan,
  licenseMode: string,
  open: boolean,
  quantity: number,
) {
  const planPriceId = `${currentPlan.name}_${licenseMode}_price`
  const [purchaseContext, setPurchaseContext] = useState<PurchaseContext>()

  const {
    data: purchasePreview,
    isFetching,
    isError: purchasePreviewIsError,
    error: purchasePreviewError,
  } = useQuery(
    ['purchase', currentPlan, licenseMode, open, quantity],
    async () => {
      if (!open) return

      return await purchaseService.getPurchasePreview(planPriceId, quantity)
    },
    {
      onError: (error) => handleError(error, 'Failed to get purchase preview'),
    },
  )

  const purchaseMutation = useMutation((context: PurchaseContext) => {
    return purchaseService.postPurchase(
      context.creatorId != null ? context.creatorId : 0,
      context.anticipatedPrice,
      context.planPrice,
      context.quantity,
    )
  }, {})

  useEffect(() => {
    if (isFetching || !purchasePreview) return

    const cxt: PurchaseContext = {
      creatorId: creator != null ? creator.id : 0,
      anticipatedPrice: purchasePreview.paymentAmount,
      planPrice: planPriceId,
      quantity,
    }

    setPurchaseContext(cxt)
  }, [isFetching, purchasePreview, creator, planPriceId, quantity])

  return useMemo(
    () => ({
      isFetching,
      purchasePreview,
      purchaseContext,
      purchaseMutation,
      purchasePreviewIsError,
      purchasePreviewError,
    }),
    [
      isFetching,
      purchasePreview,
      purchaseContext,
      purchaseMutation,
      purchasePreviewIsError,
      purchasePreviewError,
    ],
  )
}
